// src/SalesmanCustomerReport.js
import React, { useState, useEffect } from "react";
import { database, ref, onValue } from "./firebase";
import { products } from "./salesmanHelpers";
import BossNavigation from "./BossNavigation";
import { useNavigate } from "react-router-dom";

export default function SalesmanCustomerReport() {
  const [salesmanPhone, setSalesmanPhone] = useState("");
  const [customerData, setCustomerData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const navigate = useNavigate();

  // ✅ Convert Firebase "YYYY/MM/DD" → "YYYY-MM-DD"
  const normalizeDate = (dateStr) => {
    if (!dateStr) return null;
    return dateStr.replaceAll("/", "-"); // "2025/10/03" -> "2025-10-03"
  };

  useEffect(() => {
    if (!salesmanPhone) {
      setCustomerData([]);
      setRoutes([]);
      return;
    }

    const invoicesRef = ref(database, `salesmanInvoices/${salesmanPhone}`);
    onValue(invoicesRef, (snap) => {
      if (!snap.exists()) {
        setCustomerData([]);
        setRoutes([]);
        return;
      }

      const invoices = Object.values(snap.val());
      const customerMap = {};
      const routeSet = new Set();

      invoices.forEach((inv) => {
        const name = inv.customerName || "Unknown";
        const cphone = inv.customerPhone || "N/A";
        const route = inv.route || "Unassigned";
        routeSet.add(route);

        // ✅ FIX: Date filtering normalized
        const invoiceDate = normalizeDate(inv.date); // e.g. "2025-10-03"
        if (fromDate || toDate) {
          if (fromDate && invoiceDate < fromDate) return;
          if (toDate && invoiceDate > toDate) return;
        }

        if (selectedRoute && route !== selectedRoute) return;

        if (!customerMap[cphone]) {
          customerMap[cphone] = {
            name,
            phone: cphone,
            route,
            totals: Array(products.length).fill(0),
            schemes: Array(products.length).fill(0),
          };
        }

        inv.items?.forEach((item) => {
          const idx = products.indexOf(item.product);
          if (idx !== -1) {
            customerMap[cphone].totals[idx] += item.quantity || 0;
            customerMap[cphone].schemes[idx] += item.scheme || 0;
          }
        });
      });

      setCustomerData(Object.values(customerMap));
      setRoutes(Array.from(routeSet));
    });
  }, [salesmanPhone, selectedRoute, fromDate, toDate]);

  // Filter by name or phone
  const filteredData = customerData.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.phone.includes(searchTerm)
  );

  const handleCustomerClick = (customer) => {
    localStorage.setItem("selectedCustomer", JSON.stringify(customer));
    navigate("/customer-detail");
  };

  const handleOpenAllDetails = () => {
    localStorage.setItem("allCustomer", JSON.stringify(filteredData));
    navigate("/all-customer");
  };

  const handlePrint = () => {
    window.print();
  };

  const shortName = (p) => {
    if (p.includes("Premium 1 Litre")) return "Pink 1L";
    if (p.includes("Green 1 Litre")) return "Green 1L";
    if (p.includes("Premium 2 Litre")) return "Pink 2L";
    if (p.includes("Green 2 Litre")) return "Green 2L";
    if (p.includes("Premium 500ML")) return "Pink 500ml";
    if (p.includes("Green 500ML")) return "Green 500ml";
    if (p.includes("Premium 250ML")) return "Pink 250ml";
    return p;
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>📊 Salesman Customer Report</h2>
      <BossNavigation />

      {/* Controls */}
      <div style={{ marginBottom: "10px" }}>
        <input
          type="text"
          placeholder="Enter Salesman Phone"
          value={salesmanPhone}
          onChange={(e) => setSalesmanPhone(e.target.value)}
          style={{ marginRight: "10px", padding: "8px" }}
        />

        {routes.length > 0 && (
          <select
            value={selectedRoute}
            onChange={(e) => setSelectedRoute(e.target.value)}
            style={{ marginRight: "10px", padding: "8px" }}
          >
            <option value="">All Routes</option>
            {routes.map((r, i) => (
              <option key={i} value={r}>
                {r}
              </option>
            ))}
          </select>
        )}

        <label>From: </label>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          style={{ marginRight: "10px" }}
        />
        <label>To: </label>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          style={{ marginRight: "10px" }}
        />

        <input
          type="text"
          placeholder="Search by customer name or phone"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginRight: "10px", padding: "8px" }}
        />

        <button onClick={handlePrint} style={{ marginRight: "10px" }}>
          🖨 Print Report
        </button>
        <button onClick={handleOpenAllDetails}>
          📂 Open All Customer Details
        </button>
      </div>

      {/* Table */}
      {filteredData.length === 0 ? (
        <p>No data found.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table
            border="1"
            cellPadding="5"
            style={{
              borderCollapse: "collapse",
              width: "100%",
              textAlign: "center",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    position: "sticky",
                    left: 0,
                    top: 0,
                    background: "#fff",
                    zIndex: 3,
                  }}
                >
                  Name
                </th>
                {products.map((p, i) => (
                  <th
                    key={i}
                    style={{
                      position: "sticky",
                      top: 0,
                      background: "#fff",
                      zIndex: 2,
                    }}
                  >
                    {shortName(p)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((c, idx) => (
                <tr key={idx}>
                  <td
                    style={{
                      position: "sticky",
                      left: 0,
                      background: "#f9f9f9",
                      cursor: "pointer",
                      color: "blue",
                      zIndex: 1,
                    }}
                    onClick={() => handleCustomerClick(c)}
                  >
                    {c.name}
                  </td>
                  {products.map((_, i) => (
                    <td key={i}>
                      {c.totals[i] > 0 || c.schemes[i] > 0
                        ? `${c.totals[i]}/${c.schemes[i]}`
                        : "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
